<template>
  <div class="content-wrapper">
    <loading v-model:active="isLoading" :is-full-page="true" />

    <div id="survey" ref="survey">
      <div class="title">
        <!-- <h1 :class="classColor">{{ classTitle }}</h1> -->
        <h1>Outcome</h1>
      </div>

      <h4>Assessment Date: {{ assessmentDate }}</h4>

      <!-- Classification Summary Key -->
      <div class="classification-summary">
        <h3 class="summary-title">Classification <span>Overview</span></h3>
        <div class="classification-grid" :class="currentSystemKey + '-grid'">
          <div v-for="(label, index) in config.labels" :key="index" class="classification-item" :class="{
            'active': index === classIndex,
            'inactive': index !== classIndex
          }">
            <div class="classification-header">
              <span class="classification-label" :class="config.colors[index]">
                {{ label }}
              </span>
            </div>
            <p class="classification-description">
              {{ config.descriptions[index] }}
            </p>
          </div>
        </div>
      </div>

      <!-- Main System Questions -->
      <div class="assessment-section main-system-section">
        <button class="toggle-btn" @click="showMainSystemQuestions = !showMainSystemQuestions">
          <svg class="toggle-icon" :class="{ rotated: showMainSystemQuestions }" width="20" height="20"
            viewBox="0 0 24 24">
            <path fill="currentColor" d="M7.41 8.58L12 13.17l4.59-4.59L18 10l-6 6-6-6 1.41-1.42z" />
          </svg>
          {{ infoTitle }} Complexity Factors
        </button>
        <div v-show="showMainSystemQuestions" class="question__list">
          <div class="question" v-for="(question, index) of mainSystemQuestionsSorted" :key="question.id">
            <div class="question-title">
              {{ index + 1 }}. {{ question.title }}
            </div>
            <div class="response__list">
              <div class="response" v-for="response in question.responses" :key="response.id"
                :class="responseClass(response.weight, question)">
                <span>{{ response.response }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="currentSystemKey == 'ecat'" class="score">
        <h3>{{ scoreLabel }} Score: {{ score }} points
          <div class="cross-score" v-if="crossSystemScore > 0">({{ crossTitle }} Score: {{ crossSystemScore }} points)
          </div>
        </h3>
      </div>

      <!-- Cross-System Questions -->
      <div v-if="crossSystemQuestions.length" class="assessment-section cross-system-section">
        <button class="toggle-btn cross-system-toggle" :class="crossSystemTheme" @click="showCrossSystemQuestions = !showCrossSystemQuestions">
          <div class="toggle-content">
            <div class="toggle-header">
              <svg class="toggle-icon" :class="{ rotated: showCrossSystemQuestions }" width="20" height="20"
                viewBox="0 0 24 24">
                <path fill="currentColor" d="M7.41 8.58L12 13.17l4.59-4.59L18 10l-6 6-6-6 1.41-1.42z" />
              </svg>
              <span class="toggle-title">{{ crossTitle }} Complexity Factors</span>
            </div>
            <div v-if="crossSystemClassDescription" class="cross-system-description-wrapper">
              <span :class="['cross-system-description']">{{ crossTitle }} - {{crossSystemClassDescription }}</span>
            </div>
          </div>
        </button>
        <div v-show="showCrossSystemQuestions" class="question__list">
          <div class="question" v-for="(question, index) of crossSystemQuestionsSorted" :key="question.id">
            <div class="question-title">
              {{ index + 1 }}. {{ question.title }}
            </div>
            <div class="response__list">
              <div class="response" v-for="response in question.responses" :key="response.id"
                :class="responseClass(response.weight, question)">
                <span>{{ response.response }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="currentSystemKey == 'rcat'" class="score">
      <h3>{{ scoreLabel }} Score: {{ score }} points
        <div class="cross-score" v-if="crossSystemScore > 0">({{ crossTitle }} Score: {{ crossSystemScore }} points)</div>
      </h3>
    </div>

    <button class="btn img-btn" @click="saveToImage()">Download as Image</button>
  </div>
</template>

<script>
import html2canvas from 'html2canvas-pro';
import domtoimage from 'dom-to-image-more';
import download from 'downloadjs';
import Loading from 'vue-loading-overlay';

const SYSTEM_CONFIG = {
  ecat: {
    infoTitle: 'E-CAT',
    crossTitle: 'R-CAT',
    scoreLabel: 'E-CAT',
    thresholds: [5, 11],
    labels: ['Class 1', 'Class 2', 'Class 3'],
    colors: ['', 'pumpkin-yellow', 'berry-red'],
    descriptions: [
      'Class 1 (uncomplicated): This case has a low risk for adverse events. Therefore, it is suitable for treatment by undergraduates, recent graduates and dentists with limited experience in endodontics.',
      'Class 2 (moderately complex): This case has a moderate risk for complications and a compromised outcome. As such, it is suitable for treatment for practitioners with enhanced clinical experience or postgraduate training in endodontics.',
      'Class 3 (highly complex): This case has a higher risk for complications and a compromised outcome. As such, it is most suitable for treatment by specialists in endodontics.'
    ]
  },
  rcat: {
    infoTitle: 'R-CAT',
    crossTitle: 'E-CAT',
    scoreLabel: 'R-CAT',
    thresholds: [0, 1, 5],
    labels: ['No Treatment Required', 'Simple Treatment Required', 'Complex Treatment Required', 'Unfeasible To Treat'],
    colors: ['', 'pumpkin-yellow', 'pumpkin-yellow', 'berry-red'],
    descriptions: [
      'No treatment required.',
      'This case is likely to have a favourable prognosis and is feasible to treat in primary care.',
      'This case is feasible to treat. However, it should be considered to have a guarded prognosis, and treatment should be referred or managed by an experienced practitioner due to the complexities and challenges noted below.',
      'This case is likely to have a poor or hopeless prognosis due to the complexities and challenges noted below. Therefore, it should be considered to be unfeasible or impractical to treat. An extraction should be considered, or if appropriate, the tooth should be monitored without providing definitive treatment.'
    ]
  }
};

export default {
  props: {
    assessmentDate: String,
    questionList: Array
  },
  components: { Loading },
  data() {
    return {
      isLoading: false,
      score: 0,
      crossSystemScore: 0,
      mainSystemQuestions: [],
      crossSystemQuestions: [],
      showMainSystemQuestions: true,
      showCrossSystemQuestions: true
    };
  },
  computed: {
    currentSystemKey() {
      return localStorage.getItem('@app-system') === 'ecat' ? 'ecat' : 'rcat';
    },
    config() {
      return SYSTEM_CONFIG[this.currentSystemKey];
    },
    infoTitle() {
      return this.config.infoTitle;
    },
    crossTitle() {
      return this.config.crossTitle;
    },
    scoreLabel() {
      return this.config.scoreLabel;
    },
    classIndex() {
      const { thresholds } = this.config;
      for (let i = 0; i < thresholds.length; i++) {
        if (this.score <= thresholds[i]) return i;
      }
      return thresholds.length;
    },
    classTitle() {
      return this.config.labels[this.classIndex];
    },
    classColor() {
      return this.config.colors[this.classIndex];
    },
    classDescription() {
      return this.config.descriptions[this.classIndex];
    },
    mainSystemQuestionsSorted() {
      return this.mainSystemQuestions
        .slice()
        .sort((a, b) => a.position - b.position);
    },
    crossSystemQuestionsSorted() {
      return this.crossSystemQuestions
        .slice()
        .sort((a, b) => a.position - b.position);
    },
    crossSystemConfig() {
      const crossKey = this.currentSystemKey === 'ecat' ? 'rcat' : 'ecat';
      return SYSTEM_CONFIG[crossKey];
    },
    crossSystemClassIndex() {
      const { thresholds } = this.crossSystemConfig;
      for (let i = 0; i < thresholds.length; i++) {
        if (this.crossSystemScore <= thresholds[i]) return i;
      }
      return thresholds.length;
    },
    crossSystemClassLabel() {
      if (this.crossSystemScore === 0) return '';
      return this.crossSystemConfig.labels[this.crossSystemClassIndex];
    },
    crossSystemClassColor() {
      if (this.crossSystemScore === 0) return '';
      return this.crossSystemConfig.colors[this.crossSystemClassIndex];
    },
    crossSystemClassDescription() {
      if (this.crossSystemScore === 0) return '';
      return this.crossSystemConfig.descriptions[this.crossSystemClassIndex];
    },
    crossSystemTheme() {
      if (!this.crossSystemClassColor) return 'theme-neutral';
      const map = {
        'leaf-green': 'theme-green',
        'pumpkin-yellow': 'theme-yellow',
        'berry-red': 'theme-red'
      };
      return map[this.crossSystemClassColor] || 'theme-neutral';
    }
  },
  watch: {
    questionList: {
      handler(newList) {
        this.loadQuestions(newList);
      },
      immediate: true
    }
  },
  methods: {
    getScoreRange(index) {
      const { thresholds } = this.config;
      if (index === 0) {
        return `0-${thresholds[0]}`;
      } else if (index < thresholds.length) {
        return `${thresholds[index - 1] + 1}-${thresholds[index]}`;
      } else {
        return `${thresholds[thresholds.length - 1] + 1}+`;
      }
    },
    responseClass(weight, question) {
      if (question.systemType == 0) {
        if (weight == 0) return '';
        if (weight <= 5) return '';
        if (weight <= 10) return 'response-medium';
        return 'response-high';
      } else {
        if (weight < 2) return '';
        if (weight >= 2 && weight < 6) return 'response-medium';
        return 'response-high';
      }


    },
    saveToImage() {
      this.isLoading = true;
      const node = this.$refs.survey;
      if (!node) return this.handleError('Survey element not found.');
      const options = {
        quality: 0.95,
        width: node.scrollWidth,
        height: node.scrollHeight,
        bgcolor: '#ffffff',
        style: { transform: 'scale(1)', margin: '0' }
      };
      domtoimage
        .toPng(node, options)
        .then(dataUrl => {
          download(dataUrl, 'survey.png');
          this.isLoading = false;
        })
        .catch(error => this.handleError(error.message));
    },
    handleError(message) {
      alert(`Error: ${message}`);
      this.isLoading = false;
    },
    loadQuestions(questions) {
      const currentKey = this.currentSystemKey;
      if (questions == null || questions == "") return;

      const mainQs = questions.filter(q => q.systemType === (currentKey === 'ecat' ? 0 : 1));
      const crossQs = questions.filter(
        q => q.systemType !== (currentKey === 'ecat' ? 0 : 1) && q.systemType !== undefined
      );

      this.mainSystemQuestions = [];
      this.crossSystemQuestions = [];
      const curr = { value: 0 };
      const other = { value: 0 };

      const process = (list, targetArray, acc) => {
        list.forEach(q => {
          const selected = q.responses.filter(r => r.isSelected);
          if (!selected.length) return;
          q.responses = selected;
          targetArray.push(q);
          const respScore = selected.reduce((sum, r) => sum + r.weight, 0);
          acc.value += respScore;
          q.groupPosition = respScore <= 5 ? 0 : respScore <= 11 ? 1 : 2;
        });
      };

      process(mainQs, this.mainSystemQuestions, curr);
      process(crossQs, this.crossSystemQuestions, other);

      this.crossSystemScore = other.value;
      let crossComplexity = 0;
      if (other.value) crossComplexity = other.value <= 5 ? 1 : other.value <= 11 ? 2 : 2;

      this.score = curr.value + (this.crossSystemQuestions.length ? crossComplexity : 0);
      this.$emit('update:score', this.score);
      this.$emit('update:crossSystemScore', this.crossSystemScore);
    }
  }
};
</script>

<style lang="scss" scoped>
.content-wrapper {
  margin: 0 auto;

  padding-top: 0px !important;

  margin-top: 0px;

  .title {
    margin-top: 10px;
    text-align: center;

    h1 {
      font-size: 38px;
      margin: 0;
    }
  }

  .leaf-green {
    color: #2D5A27 !important;
  }

  .pumpkin-yellow {
    color: #E99A10 !important;
  }

  .berry-red {
    color: #920000 !important;
  }

  .score {
    margin: 20px 0;
    text-align: right;
    font-weight: bold;

    h3 {
      margin: 0;
      font-size: 18px;

      .cross-score {
        color: #6B7280;
        font-weight: 400;
        font-size: 0.85em;
        margin-top: 8px;
      }
    }
  }

  h4 {
    text-align: center;
    margin: 10px 0 30px 0;
    color: #6B7280;
    font-weight: normal;
  }
}

.classification-summary {
  margin: 30px 0 40px 0;
  padding: 25px;
  background: #F8FAFC;
  border-radius: 12px;
  border: 1px solid #E2E8F0;

  .summary-title {
    font-size: 22px;
    font-weight: 600;
    color: #1E293B;
    margin: 0 0 20px 0;
    text-align: center;
    padding-bottom: 12px;
    border-bottom: 2px solid #013B82;
  }

  .classification-grid {
    display: grid;
    gap: 12px;

    &.ecat-grid {
      grid-template-columns: repeat(4, 1fr);

      .classification-item {
        &.active {
          grid-column: span 2;
        }
      }
    }

    &.rcat-grid {
      grid-template-columns: repeat(5, 1fr);

      .classification-item {
        &.active {
          grid-column: span 2;
        }
      }
    }

    .classification-item {
      padding: 20px 16px;
      border-radius: 8px;
      border: 3px solid transparent;
      transition: all 0.3s ease;
      background: #FFFFFF;
      position: relative;
      overflow: hidden;
      display: flex;
      flex-direction: column;
      min-height: 180px;

      // Color-coded left border for all items
      &:before {
        content: '';
        position: absolute;
        left: 0;
        top: 0;
        bottom: 0;
        width: 6px;
        transition: all 0.3s ease;
      }

      // Default (Class 1 / No Treatment) - Green
      &:nth-child(1):before {
        background: #2D5A27;
      }

      // Moderate (Class 2 / Simple/Complex Treatment) - Yellow/Orange
      &:nth-child(2):before,
      &:nth-child(3):before {
        background: #E99A10;
      }

      // High Risk (Class 3 / Unfeasible) - Red
      &:nth-child(4):before,
      &:last-child:before {
        background: #920000;
      }

      &.active {
        border-color: #013B82;
        background: #FFFFFF;
        box-shadow: 0 6px 20px rgba(1, 59, 130, 0.2);
        transform: translateY(-3px);

        &:before {
          width: 8px;
        }

        // Enhanced color backgrounds for active state
        &:nth-child(1) {
          background: linear-gradient(135deg, #F0F9FF 0%, #E0F2FE 100%);
          border-color: #2D5A27;
        }

        &:nth-child(2),
        &:nth-child(3) {
          background: linear-gradient(135deg, #FFFBEB 0%, #FEF3C7 100%);
          border-color: #E99A10;
        }

        &:nth-child(4),
        &:last-child {
          background: linear-gradient(135deg, #FEF2F2 0%, #FECACA 100%);
          border-color: #920000;
        }

        .classification-header {
          .classification-label {
            font-weight: 700;
          }
        }

        .classification-description {
          color: #1E293B;
          font-size: 16px;
        }
      }

      &.inactive {
        background: #F8FAFC;
        opacity: 0.5;

        &:before {
          opacity: 0.3;
        }

        .classification-header {
          .classification-label {
            color: #64748B !important;
          }
        }

        .classification-description {
          color: #64748B;
        }
      }

      .classification-header {
        display: flex;
        flex-direction: column;
        align-items: center;
        margin-bottom: 12px;
        justify-content: center;
        min-height: 50px;

        .classification-label {
          font-size: 16px;
          font-weight: 600;
          transition: color 0.3s ease;
          text-align: center;
          line-height: 1.2;

          &.pumpkin-yellow {
            color: #E99A10;
            text-shadow: 0 1px 2px rgba(233, 154, 16, 0.1);
          }

          &.berry-red {
            color: #920000;
            text-shadow: 0 1px 2px rgba(146, 0, 0, 0.1);
          }

          // Default green for first item
          &:not(.pumpkin-yellow):not(.berry-red) {
            color: #2D5A27;
            text-shadow: 0 1px 2px rgba(45, 90, 39, 0.1);
          }
        }
      }

      .classification-description {
        font-size: 12px;
        line-height: 1.4;
        margin: 0;
        transition: color 0.3s ease;
        text-align: center;
        flex-grow: 1;
        display: flex;
        align-items: center;
        justify-content: center;
      }
    }
  }
}

.assessment-section {
  margin-bottom: 30px;

  .section-title {
    font-size: 20px;
    font-weight: 600;
    color: #111827;
    margin-bottom: 15px;
    padding-bottom: 8px;
    border-bottom: 2px solid #013B82;
    text-align: left;
    margin-top: 15px;
  }

  &.cross-system-section {
    .section-title {
      border-bottom: 2px solid #6B7280;
    }
  }
}

.toggle-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(135deg, #013B82, #0A2D5C);
  color: white;
  border: none;
  font-size: 20px;
  font-weight: 600;
  cursor: pointer;
  padding: 12px 16px;
  margin: 0 0 20px 0;
  transition: all 0.2s ease;
  width: 100%;
  text-align: left;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(1, 59, 130, 0.2);

  position: relative;
  overflow: hidden;
  --indicator: transparent;

  &::before {
    content: '';
    position: absolute;
    top: 0; bottom: 0; left: 0;
    width: 10px;
    background: var(--indicator);
    z-index: 1;
    pointer-events: none;
  }

  &.theme-neutral { --indicator: transparent; }
  &.theme-green   { --indicator: #2D5A27; }
  &.theme-yellow  { --indicator: #E99A10; }
  &.theme-red     { --indicator: #920000; }

  &:hover {
    background: linear-gradient(135deg, #0A2D5C, #013B82);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(1, 59, 130, 0.3);
  }
  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(1, 59, 130, 0.2);
  }

  .toggle-icon {
    transition: transform 0.2s ease;
    color: white;
    &.rotated { transform: rotate(180deg); }
  }

  &.cross-system-toggle {
    align-items: flex-start;
    .toggle-content { display: flex; flex-direction: column; width: 100%; }
    .toggle-header { display: flex; align-items: center; gap: 8px; }
    .toggle-title { font-size: 20px; font-weight: 600; color: white; }
    .cross-system-description-wrapper { margin-top: 8px; margin-left: 28px; }
    .cross-system-description { font-size: 14px; font-weight: 500; color: rgba(255,255,255,0.9); line-height: 1.4; }
  }
}


.question__list {
  margin-top: 20px;

  .question {
    margin-bottom: 15px;
    padding-bottom: 15px;
    border-bottom: 1px solid #013B82;
    text-align: left;

    &:last-child {
      border-bottom: none;
      margin-bottom: 0px;
      padding-bottom: 0px;
    }

    .question-title {
      font-weight: 600;
      font-size: 16px;
      color: #111827;
      margin-bottom: 10px;
    }

    .response__list {
      margin-top: 5px;

      .response {
        padding: 12px 16px;
        display: flex;
        align-items: center;
        margin-bottom: 8px;
        border-radius: 8px;
        border: 2px solid transparent;
        transition: all 0.3s ease;
        position: relative;
        background: #FFFFFF;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

        // Color-coded left border for all responses
        &:before {
          content: '';
          position: absolute;
          left: 0;
          top: 0;
          bottom: 0;
          width: 4px;
          transition: all 0.3s ease;
        }

        span {
          color: #1E293B;
          line-height: 1.4;
          font-weight: 500;
          margin-left: 12px;
        }

        // Low complexity (green theme)
        &.response-low {
          background: linear-gradient(135deg, #F0F9FF 0%, #E0F2FE 100%);
          border-color: #2D5A27;

          &:before {
            background: #2D5A27;
          }

          span {
            color: #1E293B;
          }
        }

        // Medium complexity (yellow/orange theme)
        &.response-medium {
          background: linear-gradient(135deg, #FFFBEB 0%, #FEF3C7 100%);
          border-color: #E99A10;

          &:before {
            background: #E99A10;
          }

          span {
            color: #1E293B;
          }
        }

        // High complexity (red theme)
        &.response-high {
          background: linear-gradient(135deg, #FEF2F2 0%, #FECACA 100%);
          border-color: #920000;

          &:before {
            background: #920000;
          }

          span {
            color: #1E293B;
          }
        }

        // Hover effects
        &:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);

          &:before {
            width: 6px;
          }
        }
      }
    }
  }
}

.img-btn {
  margin: 30px auto 20px;
  display: block;
  padding: 12px 24px;
  background: #2A292E;
  color: #FFF;
  border: none;
  border-radius: 6px;
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background: #1F1E23;
  }
}

@media (max-width: 768px) {
  .assessment-section {
    margin-bottom: 20px;
  }

  .classification-summary {
    .classification-grid {

      &.ecat-grid,
      &.rcat-grid {
        grid-template-columns: 1fr;
        gap: 8px;

        .classification-item {
          &.inactive {
            display: none; // Hide inactive classifications on mobile
          }

          &.active {
            grid-column: span 1; // Reset grid span for mobile
          }
        }
      }
    }

    .classification-item {
      padding: 20px 16px;
      min-height: 160px;

      .classification-header {
        min-height: 45px;

        .classification-label {
          font-size: 16px;
        }
      }

      .classification-description {
        font-size: 14px;
        line-height: 1.5;
      }
    }
  }

  // Mobile cross-system toggle layout
  .toggle-btn.cross-system-toggle {
    .cross-system-description-wrapper {
      margin-left: 0; // Remove left margin on mobile
      margin-top: 6px;
    }

    .cross-system-description {
      font-size: 13px;
      line-height: 1.3;
    }
  }

  .summary-title {
    span {
      display: none;
    }
  }

  // Mobile response styling adjustments
  .question__list {
    .response__list {
      .response {
        padding: 10px 14px;
        margin-bottom: 6px;

        span {
          margin-left: 10px;
          font-size: 14px;
        }
      }
    }
  }
}

@media (max-width: 480px) {
  .classification-summary {
    padding: 20px 15px;
    margin: 20px 0 30px 0;

    .summary-title {
      font-size: 20px;
      margin-bottom: 15px;
    }

    .classification-grid {
      .classification-item {
        padding: 18px 14px;
        min-height: 140px;

        .classification-header {
          margin-bottom: 10px;
          min-height: 40px;

          .classification-label {
            font-size: 15px;
          }
        }

        .classification-description {
          font-size: 13px;
          line-height: 1.4;
        }
      }
    }
  }

  .content-wrapper {
    .title h1 {
      font-size: 32px;
    }
  }

  // Smaller mobile devices - further adjust cross-system description
  .toggle-btn.cross-system-toggle {
    .cross-system-description {
      font-size: 12px;
    }
  }
}
</style>